// A Ship log row opened (spec §3.14, Figure 35): the shipment — sales orders / PTs, CTNS / PLTS,
// loader, in / out, trailer, door, carrier, tracking, notes — the line, what A2000 says of its
// pick tickets, and the line's history from the System log.

import type { ReactNode } from "react";
import {
  a2000Lines,
  a2000Text,
  dayOfWeek,
  formatDay,
  lineRef,
  poPtText,
  timeShown,
  shownCustomerText,
} from "@app/shared";
import type { PickupRecord, ShipLogRow, ShippingRowRecord } from "@app/shared";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@axny/platform-ui";
import { LineHistory, changedAt, useChanges } from "./line-history";
import { LineSourceFacts } from "./source-filter";

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-medium break-words">{children || "—"}</p>
    </div>
  );
}

export function ShipmentSheet({
  row,
  pickup,
  shippingRow,
  onClose,
}: {
  row: ShipLogRow;
  pickup: PickupRecord | null;
  shippingRow: ShippingRowRecord | null;
  onClose: () => void;
}) {
  const { line, shipment: s } = row;
  const pts = line.pts.map((p) => p.pickTicket);
  // When it was marked: its "ship" change, as the history below says it.
  const history = useChanges({ line: s.lineId }, !!s.lineId);
  const marked = history.data?.changes.find((c) => c.action === "ship");
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="shipment-sheet">
        <SheetHeader>
          <SheetTitle>{shownCustomerText(line) || "Shipment"}</SheetTitle>
          <SheetDescription>
            {poPtText(line, 3)} · {lineRef(line)}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-5 px-4 pb-6 text-sm">
          <LineSourceFacts line={line} />
          <section className="rounded-lg border border-teal-200 bg-teal-50/60 p-3">
            <p className="mb-3">
              Shipped <b>{s.shipDate ? `${dayOfWeek(s.shipDate)} ${formatDay(s.shipDate)}` : "—"}</b>
              {s.loadRef ? (
                <>
                  {" "}
                  · <b>{s.loadRef}</b>
                </>
              ) : null}
              {s.carrier ? ` · ${s.carrier}` : ""}
              {line.finished?.by ? ` · by ${line.finished.by}` : ""}
              {marked ? <span className="text-muted-foreground"> · marked {changedAt(marked)}</span> : null}
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Fact label="Sales orders / PTs">{pts.join(", ")}</Fact>
              <Fact label="CTNS / PLTS">
                {s.ctns ?? "—"} / {s.plts ?? (line.fields.pallets || "—")}
              </Fact>
              <Fact label="Loader">{s.loader}</Fact>
              <Fact label="In / Out">
                {s.inTime || s.outTime ? `${timeShown(s.inTime) || "—"} / ${timeShown(s.outTime) || "—"}` : ""}
              </Fact>
              <Fact label="Trailer#">{s.trailer}</Fact>
              <Fact label="Door">{s.door}</Fact>
              <Fact label="Tracking #">{s.tracking}</Fact>
              <Fact label="Notes">{s.notes}</Fact>
              {pickup ? (
                <Fact label="Pickup">
                  {pickup.pickupDate ? formatDay(pickup.pickupDate) : "—"}
                  {pickup.appTime ? ` · ${timeShown(pickup.appTime)}` : ""} · {pickup.status}
                </Fact>
              ) : (
                <Fact label="Pickup">Shipped outside the schedule</Fact>
              )}
              {shippingRow?.wmsAsnTrip ? <Fact label="WMS / ASN / TRIP">{shippingRow.wmsAsnTrip}</Fact> : null}
            </div>
          </section>

          <section className="grid grid-cols-3 gap-3">
            <Fact label="First ship">{line.start ? formatDay(line.start) : ""}</Fact>
            <Fact label="Cancel">{line.cancel ? formatDay(line.cancel) : ""}</Fact>
            <Fact label="QTY">{line.qty.toLocaleString("en-US")}</Fact>
            <Fact label="Warehouse">{line.whs.join(", ")}</Fact>
            <Fact label="Routing status">{line.fields.routingStatus}</Fact>
            <Fact label="Routing Reference">{line.fields.routingRef}</Fact>
          </section>

          <section>
            <h3 className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">A2000</h3>
            {line.a2000 ? (
              <>
                <p className="font-medium">{a2000Text(line.a2000)}</p>
                <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
                  {a2000Lines(line.a2000).map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-muted-foreground">Not shipped in A2000 yet (or no longer listed).</p>
            )}
          </section>

          <LineHistory lineId={s.lineId} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
